import { MySupportInfoControllerService } from '@/__generated__/FrontApi';
import Button, { BtnClose } from '@/components/Button';
import { PopCcontainer, PopupTitle } from '@/components/Css/Pop';
import Modal, { ModalProps } from '@/components/Modal';
import MyPayInformation from '@/components/Mypage/MyPayInformation';
import { Tit } from '@/components/Titles';
import {
  getPaymentTypeNameToCode,
  PAYTYPE_PAYMENT_DAY_LIST,
} from '@/helpers/PaymentHelper';
import { ContainerGroup } from '@/page-blocks/mypage/MypageCommon';
import PaymentMethodList from '@/page-blocks/mypage/PaymentMethodList';
import { usePopupStore } from '@/stores/PopupStore';
import { useUserStore } from '@/stores/UserStore';
import React, { useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

const FormContainer = styled.div`
  padding: 24px 0 40px;
`;

type PaymentMethodChangeProps = ModalProps & {
  moduleInfo: any;
  pledgeList: any;
  pledgeInfo: any;
  onSubmitAfter: (updateInfo: PaymethodUpdateResult) => void;
};

export interface PaymethodUpdateResult {
  pledgeId: string;
  dayOfMonth: string;
}

interface PaymethodUpdateForm {
  ownerType: string;
  paymentType: string;
  pledgeId: string;
  mandateId: string;
  applyAllYn: string; // 결제 수단 변경에서만 사용
  applyAllPledge: string;
  dayOfMonth: string;
}

const PaymentMethodChange: React.FC<PaymentMethodChangeProps> = ({
  onRequestClose,
  onSubmitAfter,
  moduleInfo,
  pledgeList,
  pledgeInfo,
  ...props
}) => {
  const popupStore = usePopupStore();
  const userStore = useUserStore();
  const [isPaymentMadateAdd, setPaymentMadateAdd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    setValue,
    handleSubmit,
    getValues,
  } = useForm<PaymethodUpdateForm>({
    defaultValues: {
      pledgeId: pledgeInfo.pledgeId,
      ownerType: '',
      paymentType: '',
      mandateId: '',
      dayOfMonth: '',
    },
  });

  const onSubmit: SubmitHandler<PaymethodUpdateForm> = useCallback(
    async (formData) => {
      if (!formData.mandateId) {
        onRequestClose();
        return;
      }
      const payDaysByPayType =
        PAYTYPE_PAYMENT_DAY_LIST[
        getPaymentTypeNameToCode(formData.paymentType)
      ]?.filter((item) => item.day === formData.dayOfMonth) || [];

      if (!formData.dayOfMonth || payDaysByPayType.length === 0) {
        popupStore.show('결제일을 선택한 후 변경하기를 눌러주세요.');
        return;
      }
      try {
        setIsSubmitting(true);
        const {
          resultCode,
          resultMessage,
          resultMessageDetail,
          data,
        } = await MySupportInfoControllerService.updatePledgePaymethodUsingPost(
          {
            ...formData,
            pathType: moduleInfo ? `MISSMODULE` : `SUPPORT`,
          },
        );
        if (resultCode === `success`) {
          // SF 반영 대기 후 모달 닫기
          await new Promise((r) => setTimeout(r, 2000));
          onSubmitAfter(formData);
        } else {
          popupStore.show(resultMessageDetail || `오류가 발생했습니다`);
        }
      } catch (e: any) {
        if (e.response) {
          popupStore.show(
            e.response.data.resultMessage || `오류가 발생했습니다`,
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [moduleInfo, onSubmitAfter, popupStore],
  );
  return (
    <>
      <Modal
        onRequestClose={onRequestClose}
        maxWidth="339px"
        color="sky"
        {...props}
      >
        <PopCcontainer>
          <PopupTitle>
            <h2>
              <Tit size="s3" color="sky">
                결제정보
              </Tit>
            </h2>
            <BtnClose onClick={() => onRequestClose()}>
              <span className="a11y">닫기</span>
            </BtnClose>
          </PopupTitle>
          {/* !!!! 주의 : PaymentMethodList 안에도 form이 있기때문에 감싸면 정상 작동하지 않음 */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            ref={register}
            name="frmPaymethodUpdate"
          >
            <input type="hidden" name="pledgeId" ref={register} />
            <input type="hidden" name="ownerType" ref={register} />
            <input type="hidden" name="paymentType" ref={register} />
            <input type="hidden" name="mandateId" ref={register} />
            <input type="hidden" name="applyAllYn" ref={register} />
            <input type="hidden" name="applyAllPledge" ref={register} />
            <input type="hidden" name="dayOfMonth" ref={register} />
          </form>
          <FormContainer>
            <p>원하시는 결제수단을 선택해 주세요.</p>

            <PaymentMethodList
              displayMode="list"
              isEnableOnly={false}
              autoSelect={false}
              moduleInfo={moduleInfo}
              pledgeInfo={pledgeInfo}
              onChange={(paymethod, isApplyAll) => {
                // 결제수단 선택시 결제일 설정 화면 노출
                setValue('ownerType', paymethod.ownerType || 'Self');
                setValue('paymentType', paymethod.paymentType);
                setValue('mandateId', paymethod.mandateId);
                setValue('applyAllYn', isApplyAll === true ? 'Y' : 'N');
                if (isApplyAll === true) {
                  const applyTarget = pledgeList.filter(
                    (item: any) => item.mandateUpdateYn === 'Y',
                  );
                  setValue(
                    'applyAllPledge',
                    applyTarget.map((target: any) => target.pledgeId).join(','),
                  );
                } else {
                  setValue('applyAllPledge', '');
                }
                setValue('dayOfMonth', paymethod.paymentDay || '');
              }}
              onAddMethod={() => {
                setValue('mandateId', '');
                setPaymentMadateAdd(true);
              }}
            />
            <Button
              type="button"
              full
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              {isSubmitting ? '변경 중...' : '변경하기'}
            </Button>
          </FormContainer>
        </PopCcontainer>
      </Modal>
      {isPaymentMadateAdd && (
        <Modal
          onRequestClose={() => setPaymentMadateAdd(false)}
          maxWidth="540px"
          color="sky"
        >
          <PopCcontainer>
            <PopupTitle>
              <h2>
                <Tit size="s3" color="sky">
                  결제수단 관리
                </Tit>
              </h2>
              <BtnClose onClick={() => setPaymentMadateAdd(false)}>
                <span className="a11y">닫기</span>
              </BtnClose>
            </PopupTitle>
            <ContainerGroup
              css={`
                padding-top: 20px;
              `}
            >
              <MyPayInformation
                supporterType={
                  userStore.user?.donorType === 'group' ? 'group' : 'person'
                }
                memberTypeCode={userStore.user?.donorTypeCode || 'IN'}
                payInfo={{ paymentType: getValues('paymentType') }}
                pledgeInfo={pledgeInfo}
                onSuccess={async (result) => {
                  // SF 반영 대기 후 팝업 닫기
                  await new Promise((r) => setTimeout(r, 2000));
                  setPaymentMadateAdd(false);
                  onSubmitAfter();
                }}
              />
            </ContainerGroup>
          </PopCcontainer>
        </Modal>
      )}
    </>
  );
};

export default PaymentMethodChange;
